import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TrendingUp, ShoppingCart, Clock, CheckCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { subscriptionApi, marketApi, orderApi } from '@/api';
import type { Subscription } from '@/api/subscription';
import type { System } from '@/api/market';
import type { Order } from '@/api/order';
import { btnOutlineNeon, btnPrimaryNeon } from '@/lib/admin-ui';
import { useTenantPermission } from '@/hooks/use-tenant-permission';

export function WorkspacePage() {
  const canMarket = useTenantPermission('tenant:market:view');
  const [stats, setStats] = useState({
    activeSubscriptions: 0,
    totalSpent: '¥0',
    renewalsSoon: 0,
    availableUpgrades: 0,
  });
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [systems, setSystems] = useState<System[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  const loadWorkspaceData = useCallback(async () => {
    if (!canMarket) {
      setSubscriptions([]);
      setSystems([]);
      setOrders([]);
      setStats({
        activeSubscriptions: 0,
        totalSpent: '¥0',
        renewalsSoon: 0,
        availableUpgrades: 0,
      });
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      // 加载我的订阅
      const subs = await subscriptionApi.getMySubscriptions();
      // 确保 subs 是数组
      const subsArray = Array.isArray(subs) ? subs : [];
      setSubscriptions(subsArray);

      // 加载系统列表
      const systemsData = await marketApi.getSystems();
      // 确保 systemsData 是数组
      const systemsArray = Array.isArray(systemsData) ? systemsData : [];
      setSystems(systemsArray);

      // 加载我的订单
      const ordersData = await orderApi.getMyOrders();
      const ordersArray = Array.isArray(ordersData) ? ordersData : [];
      setOrders(ordersArray);

      // 计算统计数据
      const active = subsArray.filter(s => s.status === 'ACTIVE' || s.status === 'TRIAL');
      const renewals = subsArray.filter(s => {
        const endDate = new Date(s.endAt);
        const now = new Date();
        const daysUntilRenewal = (endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
        return daysUntilRenewal <= 30 && daysUntilRenewal > 0;
      });

      // 计算可升级版本数量：对于每个已订阅的系统，检查是否有更高价格的套餐
      let upgradeCount = 0;
      active.forEach(sub => {
        const system = systemsArray.find((sys: System) => sys.id === sub.systemId);
        if (system && system.plans) {
          const currentPlan = system.plans.find(p => p.id === sub.planId);
          if (currentPlan) {
            // 检查是否有价格更高的套餐
            const hasHigherPlan = system.plans.some(p => p.price > currentPlan.price);
            if (hasHigherPlan) {
              upgradeCount++;
            }
          }
        }
      });

      // 计算总支出：统计所有已支付订单的金额总和（分转元）
      const paidOrders = ordersArray.filter(o => o.status === 'PAID');
      const totalSpentAmount = paidOrders.reduce((sum, order) => sum + order.amount, 0);

      setStats({
        activeSubscriptions: active.length,
        totalSpent: `¥${(totalSpentAmount / 100).toFixed(2)}`,
        renewalsSoon: renewals.length,
        availableUpgrades: upgradeCount,
      });
    } catch (error) {
      console.error('加载工作台数据失败:', error);
      setSubscriptions([]);
      setSystems([]);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [canMarket]);

  useEffect(() => {
    void loadWorkspaceData();
  }, [loadWorkspaceData]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">工作台总览</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            加载中...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!canMarket && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          当前角色无订阅与订单数据权限（<code className="font-mono text-amber-200/90">tenant:market:view</code>
          ），下方统计与订阅列表为空。请联系租户管理员调整角色权限。
        </div>
      )}
      {/* 页面标题 */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">工作台总览</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          查看我的订阅和系统使用情况
        </p>
      </div>

      {/* 统计卡片 */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-primary" />
              </div>
              <Badge variant="default">活跃</Badge>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">活跃订阅</p>
              <p className="mt-1 text-2xl font-bold">{stats.activeSubscriptions} 个</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <Badge variant="default">+12.5%</Badge>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">总支出</p>
              <p className="mt-1 text-2xl font-bold">{stats.totalSpent}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <Badge variant="destructive">需关注</Badge>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">即将续费</p>
              <p className="mt-1 text-2xl font-bold">{stats.renewalsSoon} 个</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-blue-600" />
              </div>
              <Badge variant="secondary">可升级</Badge>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">可升级版本</p>
              <p className="mt-1 text-2xl font-bold">{stats.availableUpgrades} 个</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 我的订阅 */}
      <Card>
        <CardHeader>
          <CardTitle>我的订阅</CardTitle>
          <CardDescription>当前已激活的系统订阅</CardDescription>
        </CardHeader>
        <CardContent>
          {subscriptions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              暂无订阅，前往应用市场购买
            </div>
          ) : (
            <div className="space-y-4">
              {subscriptions.map((sub) => {
                // 检查是否可以升级
                const system = systems.find((sys: System) => sys.id === sub.systemId);
                const currentPlan = system?.plans?.find(p => p.id === sub.planId);
                const higherPlans = system?.plans?.filter(p => (p.price || 0) > (currentPlan?.price || 0)) || [];
                const canUpgrade = higherPlans.length > 0;
                
                // 检查订阅是否过期
                const isExpired = new Date(sub.endAt) < new Date();
                
                return (
                  <div key={sub.id} className="flex items-center justify-between p-4 border rounded-lg relative overflow-hidden">
                    {/* 右上角三角形角标 */}
                    {!isExpired && (
                      <div className="absolute top-0 right-0 w-[60px] h-[60px] overflow-hidden">
                        <div className="absolute top-0 right-0 w-0 h-0 border-t-[60px] border-t-primary border-l-[60px] border-l-transparent"></div>
                        <span className="absolute top-3 right-1 text-[10px] text-primary-foreground font-medium transform rotate-45">
                          {sub.status === 'TRIAL' ? '试用' : '已激活'}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{sub.system?.name || '系统'}</p>
                        <p className="text-sm text-muted-foreground">
                          {sub.plan?.name || '版本'} · 到期：{new Date(sub.endAt).toLocaleDateString('zh-CN')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mr-8">
                      {!isExpired && system?.url && (
                        <Button
                          type="button"
                          size="sm"
                          variant="default"
                          className={btnPrimaryNeon}
                          onClick={() => system.url && window.open(system.url, '_blank')}
                        >
                          <ExternalLink className="mr-1 h-4 w-4" />
                          进入系统
                        </Button>
                      )}
                      {canUpgrade && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className={btnOutlineNeon}
                          onClick={() =>
                            toast.info('升级功能开发中，请前往应用市场重新购买')
                          }
                        >
                          <TrendingUp className="mr-1 h-4 w-4" />
                          可升级
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 系统推荐 */}
      <Card>
        <CardHeader>
          <CardTitle>系统推荐</CardTitle>
          <CardDescription>根据您的使用场景推荐</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {systems.slice(0, 2).map((sys) => (
              <div key={sys.id} className="p-4 border rounded-lg hover:border-primary/50 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium">{sys.name}</h4>
                  <Badge variant="outline">
                    {sys.plans?.[0]?.price ? `¥${sys.plans[0].price}/月` : '咨询'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{sys.description || '暂无描述'}</p>
                <button className="text-sm text-primary hover:underline">
                  了解详情 →
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
